import type { ComponentProps } from "react";
import type { Appointment } from "@/features/appointments/types/appointmentTypes";
import { businessDayBounds } from "@/features/appointments/utils/bookingTime";
import { CalendarAgenda } from "@/features/calendar-events/components/CalendarAgenda";
import type { CalendarEvent } from "@/features/calendar-events/types/calendarEventTypes";
import { Section } from "@/shared/components/ui/Section";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import { cn } from "@/shared/utils/cn";
import { formatBusinessDate } from "@/shared/utils/formatters";

interface DashboardAgendaDayProps extends Pick<ComponentProps<typeof CalendarAgenda>, "onAppointmentClick" | "onEventClick" | "onGapAdd"> {
  dateKey: string;
  /** 0 for today, 1 for tomorrow, and so on. */
  offset: number;
  timeZone: string;
  appointments: Appointment[];
  events: CalendarEvent[];
  onViewDay: () => void;
}

export function DashboardAgendaDay({ dateKey, offset, timeZone, appointments, events, onViewDay, ...handlers }: DashboardAgendaDayProps) {
  const start = businessDayBounds(dateKey, timeZone).startAt;
  const format = (options: Intl.DateTimeFormatOptions) => formatBusinessDate(start, timeZone, options);
  const label = offset === 0 ? "Today" : offset === 1 ? "Tomorrow" : format({ weekday: "long" });

  return (
    <Section
      title={
        <span className="flex items-center gap-3">
          <span aria-hidden="true" className={cn("flex size-12 shrink-0 flex-col items-center justify-center rounded-xl font-bold leading-none", offset === 0 ? "bg-primary text-primary-foreground" : "bg-secondary text-primary")}>
            <span className="text-[10px] uppercase tracking-wide">{format({ weekday: "short" })}</span>
            <span className="mt-0.5 text-lg tabular-nums">{format({ day: "numeric" })}</span>
          </span>
          <span aria-hidden="true">{label}</span>
          <span className="sr-only">{label}, {format({ weekday: "long", day: "numeric", month: "long" })}</span>
        </span>
      }
      action={<SectionAction className="mt-2" aria-label={`View ${format({ weekday: "long", day: "numeric", month: "long" })}`} onClick={onViewDay}>View day</SectionAction>}
    >
      <CalendarAgenda appointments={appointments} events={events} dateKey={dateKey} timezone={timeZone} {...handlers} />
    </Section>
  );
}
